import {
  ColDef,
  ColumnMovedEvent,
  GetRowIdFunc,
  PaginationChangedEvent,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  exportOrders,
  useFeatureflags,
  useManagePageOrders,
  useOrderAssignees,
  useOrderFormConfig,
  useReportsByOrderIds,
  useWorkspaceActiveCustomFields,
} from "@web-app/api";
import {
  Button,
  LoadingIcon,
  SelectInput,
  useAlert,
} from "@web-app/components";
import { useBrowserCache } from "@web-app/hooks";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, OrderStatusText } from "@web-app/types";
import { getRouteScheduledDates } from "@web-app/utils/helpers";
import { getUserReadableTimestringFromSeconds } from "@web-app/utils/time";

import { getRouteStatusText } from "../common/DispatchMap/utils";
import { INITIAL_APP_FILTER_DATA } from "../common/Orders/data";
import TopNavigation from "../common/TopNavigation";

import OrderDetails from "./OrderDetails";
import Table from "./Table";
import { TableRow, getInitialTableData, routeNameValueGenerator } from "./data";
import { useManagePageEvents } from "./hooks/useManagePageEvents";

const OrderTableContainer: FC = () => {
  const { customFields, isLoading: isCustomFieldsLoading } =
    useWorkspaceActiveCustomFields();

  const { hasFeature } = useFeatureflags();
  const isFormBuilderEnabled = hasFeature("formBuilder");
  const { data: orderFormConfig, isLoading: isFormConfigLoading } =
    useOrderFormConfig({ enabled: isFormBuilderEnabled });
  const formFields = useMemo(
    () => (isFormBuilderEnabled ? orderFormConfig?.fields : undefined),
    [isFormBuilderEnabled, orderFormConfig?.fields],
  );

  const { showAlertAsync } = useAlert();

  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [isColumnStateLoaded, setIsColumnStateLoaded] = useState(false);
  const [pageSize, setPageSize] = useBrowserCache("tablePageSize", 20);
  const [skip, setSkip] = useState(0);
  const { selectedSidebarOrderIds } = useDispatchStore();
  const [sortFields, setSortFields] = useState({
    actualEndDateTime: "",
    actualStartDateTime: "",
    geoDropoffDateTime: "",
    geoPickupDateTime: "",
    scheduledEndDateTime: "",
    scheduledStartDateTime: "",
  });

  const [sortParam, setSortParam] = useState<string>("");

  const [appFilter, setAppFilter] = useBrowserCache(
    "tableAppFilter",
    INITIAL_APP_FILTER_DATA,
  );

  const gridRef = useRef<AgGridReact>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const { data: orders, isLoading } = useManagePageOrders({
    appFilter,
    limit: pageSize,
    searchText,
    skip: skip,
    sortParam,
  });

  const ordersArray = useMemo(() => {
    if (!orders) return [];
    return Array.from(orders.data.values());
  }, [orders]);

  const orderIds = useMemo(() => {
    return ordersArray.map((order) => order.id);
  }, [ordersArray]);

  const { assigneesMap } = useOrderAssignees(ordersArray);

  const { data: reportsData } = useReportsByOrderIds(orderIds, {
    enabled: orderIds.length > 0,
  });

  useManagePageEvents();

  const totalPages = useMemo(() => {
    if (!orders?.totalCount) return 0;
    return Math.ceil(orders.totalCount / pageSize);
  }, [orders?.totalCount, pageSize]);

  const rowData = useMemo(() => {
    if (!orders) {
      return null;
    }

    const addedRoutes = new Set();
    const routesWithAttachments = new Set<string>();
    const rows: TableRow[] = [];

    for (const order of orders.data.values()) {
      if (order.route && (order.attachments?.length ?? 0) > 0) {
        routesWithAttachments.add(order.route.id);
      }
    }

    Array.from(orders.data.values()).forEach((order) => {
      const assignee = order.assigneeId
        ? assigneesMap.get(order.assigneeId)
        : null;
      if (!!order.route && !addedRoutes.has(order.route.id)) {
        const scheduledDates = getRouteScheduledDates(order.route, orders.data);

        // Get all orders for this route to calculate duration
        const routeDuration = order.route.routeDuration
          ? getUserReadableTimestringFromSeconds(order.route.routeDuration)
          : null;

        rows.push({
          ...order.route,
          assignee,
          entity: "route",
          hasAttachments: routesWithAttachments.has(order.route.id),
          // @ts-ignore - adding routeDuration property
          routeDuration,
          scheduledEndDateTime: scheduledDates.endDate,
          scheduledStartDateTime: scheduledDates.startDate,
          // @ts-ignore - getRouteStatusText returns a string
          statusText: getRouteStatusText(
            order?.route?.status,
            !!order?.route?.assigneeId,
          ) as OrderStatusText | null,
        });
        addedRoutes.add(order.route.id);
      }
      const reportCount = reportsData?.[order.id]?.length || 0;
      rows.push({
        ...order,
        assignee,
        entity: "order",
        reportCount,
      });
    });
    return rows;
  }, [assigneesMap, orders, reportsData]);

  const defaultColDef = useMemo(() => {
    return {
      // allow every column to be grouped
      enablePivot: false,
      enableRowGroup: false,
      flex: 1,
      minWidth: 100,
      sortable: false,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        checkbox: true,
        suppressCount: true,
      },
      headerCheckboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true,
      headerName: "ID",
      lockPosition: "left",
      minWidth: 200,
      pinned: "left",
      showDisabledCheckboxes: true,
      valueGetter: routeNameValueGenerator,
    };
  }, []);

  const updateGridState = () => {
    const columnState = localStorage.getItem("columnDefsManagePage");

    if (columnState) {
      gridRef.current?.api?.applyColumnState({
        applyOrder: true,
        state: JSON.parse(columnState),
      });
    }
  };

  const setColumnStates = () => {
    const columnState = gridRef?.current?.api?.getColumnState();
    localStorage.setItem("columnDefsManagePage", JSON.stringify(columnState));
  };

  const setColumnDefsAndPersist = (defs: ColDef[]) => {
    setColumnDefs(defs);
    localStorage.setItem(
      "columnDefsManagePage",
      JSON.stringify(
        defs
          .map((col) => {
            const colId = col.colId ?? col.field;
            if (!colId) return null;
            return {
              colId,
              flex: col.flex,
              hide: col.hide,
              pinned: col.pinned,
              sort: col.sort,
              sortIndex: col.sortIndex,
              width: col.width,
            };
          })
          .filter(Boolean),
      ),
    );
  };

  const handleUpdateVisibleColumns = () => {
    setColumnStates();
    const defs = gridRef?.current?.api?.getColumnDefs();
    // @ts-ignore - ColumnDefs can be a mix of ColDef and ColGroupDef with any types
    setColumnDefs(defs);
  };

  const handleColumnMoved = (params: ColumnMovedEvent) => {
    if (params.source != "api") {
      if (params.finished) {
        setColumnStates();
      }
      const defs = gridRef?.current?.api?.getColumnDefs();
      // @ts-ignore - ColumnDefs can be a mix of ColDef and ColGroupDef with any types
      setColumnDefs(defs);
    }
  };

  const handleColumnResize = debounce(() => {
    setColumnStates();
  }, 500);

  const handlePaginationChange = (params: PaginationChangedEvent) => {
    const currentPageSize = gridRef?.current?.api.paginationGetPageSize();
    setPageSize(currentPageSize || 20);
    if (params.newData != undefined) {
      gridRef.current?.api?.deselectAll();
    }
  };

  const onSelectionChange = () => {
    let selectedRows = gridRef.current?.api.getSelectedRows();
    if (selectedRows?.length) {
      const selectedRouteIds = selectedRows
        .filter((row) => row.entity === "route")
        .map((route) => route.id);
      useDispatchStore.setState({
        selectedSidebarRouteIds: selectedRouteIds,
      });
      selectedRows = selectedRows.filter((row) => row.entity === "order");
      if (selectedRows.length > 1) {
        useDispatchStore.setState({
          selectedSidebarOrderIds: selectedRows.map((row) => row.id),
        });
      } else if (selectedRows.length === 1) {
        const order = selectedRows[0];
        if (order && order.id) {
          useDispatchStore.setState({
            selectedSidebarOrderIds: [order.id],
          });
        }
      }
    } else {
      useDispatchStore.setState({
        selectedSidebarOrderIds: [],
        selectedSidebarRouteIds: [],
      });
    }
  };

  const onExport = async () => {
    const allDisplayedColumns = gridRef?.current?.api?.getAllDisplayedColumns();
    const visibleColumns =
      allDisplayedColumns?.map((col) => col.getColDef().headerName || "") ?? [];
    const location: string = await showAlertAsync(
      exportOrders(
        {
          assigneeIds: appFilter.assigneeIds,
          countryCode: appFilter.countryCode,
          driverStatus: appFilter.driverStatus,
          orderDeliveryType: appFilter.orderDeliveryType,
          orderStatus: appFilter.orderStatus,
          scheduledDateTimeEnd: appFilter.scheduledDateTimeEnd,
          scheduledDateTimeStart: appFilter.scheduledDateTimeStart,
          stateIds: appFilter.stateIds,
          workspaceIds: appFilter.workspaceIds,
        },
        visibleColumns,
      ),
      {
        error: {
          message: "Error exporting orders",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Exporting orders...",
          title: "Exporting",
          type: "default",
        },
        success: {
          message: "Orders exported successfully",
          title: "Success",
          type: "success",
        },
      },
    );

    if (location) {
      window.open(location, "_blank");
    }
  };

  const searchOrders = (searchText: string) => {
    setSearchText(searchText);
  };

  const debouncedSearchOrders = debounce(searchOrders, 500);

  const getDataPath = useCallback((data: Order) => {
    if (data.route) {
      return [data.route.id, data.id];
    }
    return [data.id];
  }, []);

  const getRowId: GetRowIdFunc<Order> = (params) => {
    return params.data.id;
  };

  const updateSortFields = (
    sortFieldsData: { colId: string; sort: string | null | undefined }[],
  ) => {
    setSortFields((prev) => ({
      ...prev,
      ...Object.fromEntries(
        sortFieldsData.map(({ colId, sort }) => [colId, sort || ""]),
      ),
    }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
      setSkip(newPage * pageSize);
      // Reset selection when changing pages
      useDispatchStore.setState({
        selectedSidebarOrderIds: [],
        selectedSidebarRouteIds: [],
      });
      gridRef.current?.api?.deselectAll();
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSkip(0);
    // Reset selection when changing page size
    useDispatchStore.setState({
      selectedSidebarOrderIds: [],
      selectedSidebarRouteIds: [],
    });
    gridRef.current?.api?.deselectAll();
  };

  useEffect(() => {
    if (Object.values(sortFields).every((value) => value === "")) {
      setSortParam("");
      return;
    }

    let params = "";
    Object.entries(sortFields).forEach(([key, value]) => {
      if (value) {
        params += `${key}:${value},`;
      }
    });

    setSortParam(params.slice(0, -1));
  }, [sortFields]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      if (isLoading || !orders) {
        gridRef.current.api?.showLoadingOverlay();
      } else {
        gridRef.current.api?.hideOverlay();
      }
    }
  }, [isLoading, orders]);

  useEffect(() => {
    setCurrentPage(0);
    setSkip(0);
    useDispatchStore.setState({
      selectedSidebarOrderIds: [],
      selectedSidebarRouteIds: [],
    });
    gridRef.current?.api?.deselectAll();
  }, [appFilter, searchText, sortParam]);

  useEffect(() => {
    if (
      !isCustomFieldsLoading &&
      !(isFormBuilderEnabled && isFormConfigLoading) &&
      gridRef.current
    ) {
      setColumnDefs(getInitialTableData(customFields, formFields));
      setIsColumnStateLoaded(false);
    }
  }, [
    isCustomFieldsLoading,
    customFields,
    isFormBuilderEnabled,
    isFormConfigLoading,
    formFields,
  ]);

  useEffect(() => {
    if (columnDefs.length && !isColumnStateLoaded) {
      updateGridState();
      setIsColumnStateLoaded(true);
    }
  }, [columnDefs, isColumnStateLoaded]);

  return (
    <div className={clsx("h-[calc(100%-50px)] w-full")}>
      <TopNavigation
        columnDefs={columnDefs}
        setColumnDefs={setColumnDefsAndPersist}
        onExport={onExport}
        appFilter={appFilter}
        setAppFilter={setAppFilter}
        isDateRange
        debouncedSearchOrders={debouncedSearchOrders}
      />
      <div className="mt-4 h-[calc(100%-50px)] w-full ag-theme-quartz">
        <Table
          rowHeight={70}
          defaultColDef={defaultColDef}
          // @ts-ignore - columnDefs is an array of ColDef
          columnDefs={columnDefs}
          rowData={rowData}
          // @ts-ignore - autoGroupColumnDef is an object
          autoGroupColumnDef={autoGroupColumnDef}
          pagination
          paginationPageSizeSelector={[20, 50, 100, 1000]}
          paginationPageSize={pageSize}
          suppressPaginationPanel
          // @ts-ignore - handlePaginationChange is a function that returns a void
          onPaginationChanged={handlePaginationChange}
          rowSelection={"multiple"}
          suppressRowDeselection={true}
          // @ts-ignore - onSelectionChanged is a function that returns a void
          onSelectionChanged={onSelectionChange}
          ref={gridRef}
          onColumnVisible={handleUpdateVisibleColumns}
          // @ts-ignore - handleColumnMoved is a function that returns a void
          onColumnMoved={handleColumnMoved}
          onColumnResized={handleColumnResize}
          // @ts-ignore - getRowId is a function that returns a string
          getRowId={getRowId}
          treeData={true}
          // @ts-ignore - getDataPath is a function that returns a Order
          getDataPath={getDataPath}
          groupSelectsChildren={true}
          loadingOverlayComponent={() => <LoadingIcon className="h-10 w-10" />}
          suppressContextMenu={true}
          onSortChanged={(param) => {
            const sortModel = param.api
              .getColumnState()
              .filter((col) => col.colId in sortFields)
              .map(({ colId, sort }) => ({ colId, sort }));

            if (sortModel.length > 0) {
              updateSortFields(sortModel);
            }
          }}
          suppressSorting={true}
          suppressMultiSorting={false}
        />
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              Page {currentPage + 1} of {totalPages}
            </span>
            <span className="text-sm text-gray-600">
              ({orders?.totalCount ?? 0} total orders)
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
                plain
                icon="fasChevronsLeft"
              />
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                plain
                icon="fasChevronLeft"
              />
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                plain
                icon="fasChevronRight"
              />
              <Button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                plain
                icon="fasChevronsRight"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Orders per page</span>
              <div className="w-20 flex">
                <SelectInput
                  options={[
                    { label: "20", value: "20" },
                    { label: "50", value: "50" },
                    { label: "100", value: "100" },
                  ]}
                  required
                  selected={pageSize.toString()}
                  onChange={(value) => handlePageSizeChange(Number(value))}
                  variant="inline"
                  dropdownPlacement="top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedSidebarOrderIds.length === 1 &&
        orders?.data.get(selectedSidebarOrderIds[0]) && (
          <OrderDetails
            order={orders?.data.get(selectedSidebarOrderIds[0]) as Order}
          />
        )}
    </div>
  );
};

export default OrderTableContainer;
